import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, WifiOff, RefreshCw, Ban, CheckCircle2, Eye, Trash2, Download } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import LoadError from "@/components/dashboard/LoadError";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import { listCompanies, suspendCompany } from "@/api/admin";
import { toastError, toastSuccess } from "@/lib/toast";
import { formatDate } from "@/lib/dates";
import { downloadCsv } from "@/lib/csv";

const DEMO = [
  { id: "c1", name: "Acme Inc.", slug: "acme-inc", is_active: true, billing_email: "billing@acme.com", created_at: "2026-05-01T10:00:00Z", seats: 4, runs: 226, plan_id: "starter", status: "active" },
  { id: "c2", name: "Globex", slug: "globex", is_active: true, billing_email: "ops@globex.com", created_at: "2026-06-10T10:00:00Z", seats: 12, runs: 1840, plan_id: "growth", status: "active" },
  { id: "c3", name: "Initech", slug: "initech", is_active: false, billing_email: "admin@initech.com", created_at: "2026-04-12T10:00:00Z", seats: 2, runs: 40, plan_id: "free_trial", status: "trialing" },
];

const PLAN_BADGE = {
  free_trial: "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300",
  starter: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  growth: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300",
  custom: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
};

const fmtDate = (v) => formatDate(v);

const STATUS_VALUES = ["active", "trialing", "past_due", "canceled", "suspended"];

// A real uploaded logo, falling back to the gradient-initial avatar — the
// table only ever rendered the initials, even for a company with a logo_url
// set (#77). Its own component (not inline in the column's render function)
// so a broken image URL can fall back per-row via local state, the same
// pattern Sidebar.jsx already uses for this exact field.
function CompanyAvatar({ c }) {
  const [failed, setFailed] = useState(false);
  const showLogo = c.logo_url && !failed;
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-xs font-bold text-white">
      {showLogo ? (
        <img src={c.logo_url} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : (
        (c.name || "?").slice(0, 2).toUpperCase()
      )}
    </span>
  );
}

export default function Companies() {
  const navigate = useNavigate();
  const { data, loading, demo, error, reload } = useApiData(listCompanies, DEMO);
  const [busyId, setBusyId] = useState(null);
  const companies = (Array.isArray(data) ? data : []).map((c) => ({
    ...c,
    state: !c.is_active ? "suspended" : String(c.status || "active").toLowerCase(),
  }));
  // How many companies each status chip would leave you with — the chips
  // rendered label-only before, with no way to tell "Active" would narrow the
  // table to 5 rows versus 50 without actually clicking it.
  const statusCounts = companies.reduce((acc, c) => {
    acc[c.state] = (acc[c.state] || 0) + 1;
    return acc;
  }, {});
  const statusFilterOptions = STATUS_VALUES.map((s) => ({ value: s, label: s, count: statusCounts[s] || 0 }));

  // POST /companies/{id}/suspend FLIPS the flag rather than setting it, so a
  // double-click suspends and then immediately reactivates — leaving the row
  // looking untouched. Ignore clicks while one is already in flight.
  async function toggleSuspend(c) {
    if (busyId) return;
    setBusyId(c.id);
    try {
      await suspendCompany(c.id);
      await reload();
    } catch (e) {
      toastError(e.message || "Couldn't update the company.");
    } finally {
      setBusyId(null);
    }
  }

  const open = (c) => navigate(`/admin/companies/${c.id}`);

  const ActionBtn = ({ title, onClick, children, tone, disabled, busy }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      disabled={disabled}
      className={`grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 ${tone === "rose" ? "hover:border-rose-300 hover:text-rose-600" : "hover:border-brand-300 hover:text-brand-600"}`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : children}
    </button>
  );

  const columns = [
    { key: "name", header: "Company", sortable: true, render: (c) => (
      <div className="flex items-center gap-2.5">
        <CompanyAvatar c={c} />
        <div><p className="font-medium text-slate-800 dark:text-white">{c.name}</p><p className="font-mono text-xs text-slate-400">{c.slug || "—"}</p></div>
      </div>
    ) },
    { key: "billing_email", header: "Owner", render: (c) => <span className="text-sm text-slate-600 dark:text-slate-300">{c.billing_email || "—"}</span> },
    { key: "plan_id", header: "Plan", render: (c) => <Badge className={`uppercase ${PLAN_BADGE[c.plan_id] || PLAN_BADGE.free_trial}`}>{String(c.plan_id || "free_trial").replace(/_/g, " ")}</Badge> },
    { key: "seats", header: "Users", align: "right", sortable: true, render: (c) => <span className="font-semibold tabular-nums text-slate-800 dark:text-white">{c.seats}</span> },
    { key: "created_at", header: "Start date", sortable: true, render: (c) => <span className="text-sm text-slate-500 dark:text-slate-400">{fmtDate(c.created_at)}</span> },
    { key: "state", header: "Status", render: (c) => c.is_active
      ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-current" /> {c.state}</Badge>
      : <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"><span className="h-1.5 w-1.5 rounded-full bg-current" /> suspended</Badge> },
    { key: "actions", header: "Actions", align: "right", render: (c) => (
      <div className="flex justify-end gap-1.5">
        <ActionBtn title="View" onClick={() => open(c)}><Eye size={14} /></ActionBtn>
        <ActionBtn title={c.is_active ? "Suspend" : "Reactivate"} onClick={() => toggleSuspend(c)} disabled={!!busyId} busy={busyId === c.id}>{c.is_active ? <Ban size={14} /> : <CheckCircle2 size={14} />}</ActionBtn>
        <ActionBtn title="Delete" tone="rose" onClick={() => navigate(`/admin/companies/${c.id}?tab=danger`)}><Trash2 size={14} /></ActionBtn>
      </div>
    ) },
  ];

  // The full loaded list, not whatever the table's own search/filter chips
  // currently narrow it to — DataTable owns that state internally and
  // doesn't expose it to the page.
  function exportCsv() {
    const rows = [
      ["Company", "Slug", "Owner email", "Plan", "Users", "Start date", "Status"],
      ...companies.map((c) => [c.name, c.slug || "", c.billing_email || "", c.plan_id || "", c.seats ?? "", fmtDate(c.created_at), c.state]),
    ];
    downloadCsv(rows, `companies-${new Date().toISOString().slice(0, 10)}.csv`);
    toastSuccess(`Exported ${companies.length} ${companies.length === 1 ? "company" : "companies"}.`);
  }

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle={loading || error
          ? "Tenant directory."
          : `${companies.length} ${companies.length === 1 ? "tenant" : "tenants"} on the platform.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={companies.length === 0}>
              <Download size={16} /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={16} /> Refresh</Button>
          </div>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      <LoadError message={error} onRetry={reload} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <DataTable
          columns={columns}
          rows={companies}
          getRowId={(c) => c.id}
          onRowClick={open}
          searchKeys={["name", "billing_email"]}
          filters={[
            { key: "state", label: "Status", total: companies.length, options: statusFilterOptions },
            { key: "plan_id", label: "Plan", options: ["free_trial", "starter", "growth", "custom"] },
          ]}
          maxHeight="40rem"
          pageSize={10}
          emptyLabel="No companies yet."
        />
      )}
    </>
  );
}
